import { useEffect, useState } from 'react';
import { useAnimationsEnabled } from '@/utils/animationPreference';
import { usePrefersReducedMotion } from '@/features/home/guestHero/useStillHero';

/** JS-driven offer motion (count-ups) follows the same switches as the CSS. */
export function useOfferMotion(): boolean {
  const [enabled] = useAnimationsEnabled();
  const reduced = usePrefersReducedMotion();
  return enabled && !reduced;
}

/** A clock for the countdowns; 30s is enough for minute labels. */
export function useOfferNow(intervalMs = 30_000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), intervalMs);
    return () => window.clearInterval(timer);
  }, [intervalMs]);
  return now;
}

/** Counts from 0 to `target` once when it appears (or shows it at once without motion). */
export function useRevealNumber(target: number, animate: boolean, duration = 900): number {
  const [shown, setShown] = useState(animate ? 0 : target);
  useEffect(() => {
    if (!animate) {
      setShown(target);
      return undefined;
    }
    const start = performance.now();
    let raf = 0;
    const tick = (now: number) => {
      const p = Math.min((now - start) / duration, 1);
      setShown(target * (1 - Math.pow(1 - p, 3)));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, animate, duration]);
  return shown;
}
